/**
 * ============================================================
 * KEJJA OS — src/hooks/useFinance.ts  (v1.1)
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '@/lib/financeApi';
import type {
  FinanceAccountType, FinanceCategoryKind, FinanceDirection,
  BillCadence, CreditKind,
} from '@/lib/financeApi';

// ============================================================
// QUERIES
// ============================================================

export function usePulse(homeId: string | undefined, currencyCode = 'KES') {
  return useQuery({
    queryKey: ['finance', homeId, 'pulse', currencyCode],
    queryFn: () => financeApi.pulse(homeId!, currencyCode),
    enabled: Boolean(homeId),
    refetchOnWindowFocus: true,
    staleTime: 10 * 1000,   // 10 seconds — the dashboard should feel live
  });
}

export function useFinanceOverview(homeId: string | undefined, year?: number, month?: number) {
  return useQuery({
    queryKey: ['finance', homeId, 'overview', year, month],
    queryFn: () => financeApi.overview(homeId!, year, month),
    enabled: Boolean(homeId),
  });
}

export function useFinanceAccounts(homeId: string | undefined) {
  return useQuery({
    queryKey: ['finance', homeId, 'accounts'],
    queryFn: () => financeApi.listAccounts(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useFinanceCategories(homeId: string | undefined) {
  return useQuery({
    queryKey: ['finance', homeId, 'categories'],
    queryFn: () => financeApi.listCategories(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useFinanceTransactions(
  homeId: string | undefined,
  filter: {
    limit?: number; offset?: number; fromDate?: string; toDate?: string;
    categoryId?: string; accountId?: string; direction?: FinanceDirection;
  } = {},
) {
  return useQuery({
    queryKey: ['finance', homeId, 'transactions', filter],
    queryFn: () => financeApi.listTransactions(homeId!, filter),
    enabled: Boolean(homeId),
  });
}

export function useFinanceBudgets(homeId: string | undefined, year: number, month: number) {
  return useQuery({
    queryKey: ['finance', homeId, 'budgets', year, month],
    queryFn: () => financeApi.listBudgets(homeId!, year, month),
    enabled: Boolean(homeId),
  });
}

export function useFinanceBills(homeId: string | undefined) {
  return useQuery({
    queryKey: ['finance', homeId, 'bills'],
    queryFn: () => financeApi.listBills(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useFinanceFunds(homeId: string | undefined) {
  return useQuery({
    queryKey: ['finance', homeId, 'funds'],
    queryFn: () => financeApi.listFunds(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useFinanceCreditLines(homeId: string | undefined) {
  return useQuery({
    queryKey: ['finance', homeId, 'creditLines'],
    queryFn: () => financeApi.listCreditLines(homeId!),
    enabled: Boolean(homeId),
  });
}

// ============================================================
// MUTATIONS
// ============================================================

function invalidateFinance(qc: ReturnType<typeof useQueryClient>, homeId: string) {
  qc.invalidateQueries({ queryKey: ['finance', homeId] });
}

export function useSeedFinance(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => financeApi.seed(homeId!),
    onSuccess: async () => {
      if (!homeId) return;
      // Hard reset — force every finance query to refetch immediately
      await qc.invalidateQueries({ queryKey: ['finance', homeId] });
      await qc.refetchQueries({ queryKey: ['finance', homeId, 'categories'] });
      await qc.refetchQueries({ queryKey: ['finance', homeId, 'accounts'] });
      await qc.refetchQueries({ queryKey: ['finance', homeId, 'pulse'] });
    },
  });
}

// Transactions
export function useCreateTransaction(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof financeApi.createTransaction>[1]) =>
      financeApi.createTransaction(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useUpdateTransaction(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ txId, patch }: { txId: string; patch: Parameters<typeof financeApi.updateTransaction>[2] }) =>
      financeApi.updateTransaction(homeId!, txId, patch),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteTransaction(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (txId: string) => financeApi.deleteTransaction(homeId!, txId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// M-Pesa
export function useMpesaPreview(homeId: string | undefined) {
  return useMutation({
    mutationFn: (rawSms: string) => financeApi.mpesaPreview(homeId!, rawSms),
  });
}
export function useMpesaImport(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rawSms, mpesaAccountId }: { rawSms: string; mpesaAccountId?: string | null }) =>
      financeApi.mpesaImport(homeId!, rawSms, mpesaAccountId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Categories
export function useCreateCategory(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; kind: FinanceCategoryKind; color?: string | null;
      icon?: string | null; keywords?: string[];
    }) => financeApi.createCategory(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteCategory(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (catId: string) => financeApi.deleteCategory(homeId!, catId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Accounts
export function useCreateAccount(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; accountType: FinanceAccountType;
      currencyCode?: string; openingBalance?: number;
      institution?: string | null; color?: string | null; icon?: string | null;
    }) => financeApi.createAccount(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Budgets
export function useUpsertBudget(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      categoryId: string; amount: number; currencyCode?: string;
      periodYear: number; periodMonth: number; note?: string | null;
    }) => financeApi.upsertBudget(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteBudget(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (budgetId: string) => financeApi.deleteBudget(homeId!, budgetId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Bills
export function useCreateBill(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; amount: number; currencyCode?: string;
      cadence: BillCadence; nextDueDate: string;
      categoryId?: string | null; isHardBill?: boolean;
      note?: string | null; icon?: string | null; color?: string | null;
    }) => financeApi.createBill(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useUpdateBill(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, patch }: { billId: string; patch: Parameters<typeof financeApi.updateBill>[2] }) =>
      financeApi.updateBill(homeId!, billId, patch),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useMarkBillPaid(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (billId: string) => financeApi.markBillPaid(homeId!, billId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteBill(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (billId: string) => financeApi.deleteBill(homeId!, billId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Funds
export function useCreateFund(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string; targetAmount: number; savedAmount?: number;
      currencyCode?: string; targetDate?: string | null;
      priority?: number; icon?: string | null; color?: string | null;
    }) => financeApi.createFund(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useContributeToFund(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fundId, delta }: { fundId: string; delta: number }) =>
      financeApi.contributeToFund(homeId!, fundId, delta),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteFund(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fundId: string) => financeApi.deleteFund(homeId!, fundId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}

// Credit lines
export function useCreateCreditLine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: CreditKind; name: string;
      limitAmount?: number; usedAmount?: number;
    }) => financeApi.createCreditLine(homeId!, body),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useUpdateCreditLine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ creditId, patch }: { creditId: string; patch: Parameters<typeof financeApi.updateCreditLine>[2] }) =>
      financeApi.updateCreditLine(homeId!, creditId, patch),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
export function useDeleteCreditLine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creditId: string) => financeApi.deleteCreditLine(homeId!, creditId),
    onSuccess: () => { if (homeId) invalidateFinance(qc, homeId); },
  });
}
